import { useEffect, useState, useRef } from "react";

import { Container, Grid } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { ReadyState } from "react-use-websocket";

import SummaryCards from "./components/SummaryCards";
import GlobalPeriodFilter from "./components/GlobalPeriodFilter";
import PeriodIndicatorsAccordion from "./components/PeriodIndicatorsAccordion";
import CardTopUsersAccessChart from "./components/CardTopUsersCharts";
import TopFeaturesChart from "./components/CardTopFeaturesCharts";
import MonthlyHistoryTable from "./components/MonthlyHistoryTable";
import DetailHistoryModal from "./components/DetailHistoryModal";
import exportDashboardToXlsx from "./components/exportDashboardToXlsx";
import { formatNumber, gerarMesesDoFiltro } from "./components/utils";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import useWebsocketLogDashboard from "hooks/useWebsocketLogDashboard";

import HeaderClientes from "components/_refactor/headers/clients.js";

import Api from "lib/api/httpRequestHelper";
import moment from "moment";
import { IsMobile900 } from "utils/is-mobile";
import {
  DEFAULT_PERIOD_OPTION,
  getMomentsForPresetDayCount,
  parseStoredPeriodDate,
} from "components/_refactor/fields/period-field";

function resolvePeriodValue(period) {
  if (period == null) {
    return DEFAULT_PERIOD_OPTION.value;
  }

  if (typeof period === "object") {
    return period.value;
  }

  return period;
}

function getPeriodParams(period, initDate, endDate, type) {
  const prefix = type === "user" ? "&dataInicial=" : "?dataInicial=";
  const periodValue = resolvePeriodValue(period);

  if (periodValue === "Outros") {
    const mIni = parseStoredPeriodDate(initDate);
    const mFim = parseStoredPeriodDate(endDate);

    if (mIni?.isValid() && mFim?.isValid()) {
      return (
        prefix +
        mIni.format("MM-DD-YYYY") +
        "&dataFinal=" +
        mFim.format("MM-DD-YYYY")
      );
    }
  }

  const days = Number(periodValue) || DEFAULT_PERIOD_OPTION.value;
  const { mIni, mFim } = getMomentsForPresetDayCount(days);

  return (
    prefix +
    mIni.format("MM-DD-YYYY") +
    "&dataFinal=" +
    mFim.format("MM-DD-YYYY")
  );
}

function hasModalScreenFilters(screen, user, selectedSystem) {
  return !!(
    screen?.Tela ||
    user?.IdUsuario != null ||
    selectedSystem?.value ||
    screen?.Sistema
  );
}

function buildModalQueryString({
  page,
  pageSize,
  period,
  initialDate,
  finalDate,
  screen,
  user,
  selectedSystem,
}) {
  let query = getPeriodParams(period, initialDate, finalDate, "screen");
  query += `&page=${page}&pageSize=${pageSize}`;

  if (screen?.Tela) {
    query += `&tela=${encodeURIComponent(screen.Tela)}`;
  }

  if (user?.IdUsuario != null) {
    query += `&idUsuario=${user.IdUsuario}`;
  }

  const sistema = selectedSystem?.value || screen?.Sistema;
  if (sistema) {
    query += `&sistema=${encodeURIComponent(sistema)}`;
  }

  return query;
}

const MODAL_PAGE_SIZE = 10;

function getModalEndpoint(tab) {
  return tab === "2" ? "log-access-user-list" : "log-access-screen-list";
}

function parseUltimoAcesso(dataHoraUltimoAcesso) {
  if (!dataHoraUltimoAcesso) {
    return { data: "—", hora: "" };
  }

  const [data, horaParte] = dataHoraUltimoAcesso.split(" às ");

  return {
    data: data?.trim() || "—",
    hora: horaParte?.trim() || "",
  };
}

function isEmptyApiItem(item) {
  return (
    item?.MessageResponse === "Nenhum registro localizado." ||
    item?.MessageResponse === "Falha em validação de filtros!"
  );
}

function mapScreenItemsToLogs(items, pageOffset = 0) {
  return items.map((item, index) => ({
    Id: item.Id || `screen-${pageOffset + index}`,
    Acao: item.Tela || "Tela sem nome",
    Data: "—",
    Hora: "",
    Usuario: "Resumo por tela",
    Sistema: item.Sistema || "",
    DetalhesExtras: [
      `Quantidade de acessos: ${formatNumber(item.QuantidadeAcesso)}`,
      item.Sistema ? `Sistema: ${item.Sistema}` : null,
    ].filter(Boolean),
  }));
}

function mapScreenListToTimeline(result, page = 0, pageSize = MODAL_PAGE_SIZE) {
  if (result?.ListaLogsTela) {
    const items = (result.ListaLogsTela ?? []).filter(
      (item) => item && !isEmptyApiItem(item),
    );

    return {
      logs: mapScreenItemsToLogs(items, page * pageSize),
      total: result.TotalRegistros ?? items.length,
      page: result.Pagina ?? page,
    };
  }

  const allItems = (Array.isArray(result) ? result : []).filter(
    (item) => item && !isEmptyApiItem(item),
  );
  const start = page * pageSize;
  const pageItems = allItems.slice(start, start + pageSize);

  return {
    logs: mapScreenItemsToLogs(pageItems, start),
    total: allItems.length,
    page,
  };
}

function mapScreenListFromCache(cachedItems, page, pageSize = MODAL_PAGE_SIZE) {
  const start = page * pageSize;
  const pageItems = cachedItems.slice(start, start + pageSize);

  return {
    logs: mapScreenItemsToLogs(pageItems, start),
    total: cachedItems.length,
    page,
  };
}

function mapUserListToTimeline(result) {
  const items = (result?.ListaLogsUsuario ?? []).filter(
    (item) => item && !isEmptyApiItem(item),
  );

  return {
    logs: items.map((item, index) => {
      const { data: dataAcesso, hora: horaAcesso } = parseUltimoAcesso(
        item.DataHoraUltimoAcesso,
      );

      return {
        Id: item.Id || `user-${item.IdUsuario}-${index}`,
        Acao: item.TelaMaisAcessada || "Acesso ao portal",
        Data: dataAcesso,
        Hora: horaAcesso,
        Usuario: item.Nome || item.Login || "Usuário",
        Sistema: item.Sistema || "",
        DetalhesExtras: [
          item.Login ? `Login: ${item.Login}` : null,
          `Quantidade de acessos: ${formatNumber(item.QuantidadeAcesso)}`,
          item.DataHoraUltimoAcesso
            ? `Último acesso: ${item.DataHoraUltimoAcesso}`
            : null,
        ].filter(Boolean),
      };
    }),
    total: result?.TotalRegistros ?? items.length,
    page: result?.Pagina ?? 0,
  };
}

function mapModalResultToTimeline(
  tab,
  result,
  page = 0,
  pageSize = MODAL_PAGE_SIZE,
) {
  if (result?.Logs?.length) {
    return {
      logs: result.Logs,
      total: result.TotalRegistros ?? result.Logs.length,
      page: result.Pagina ?? page,
    };
  }

  if (tab === "2") {
    return mapUserListToTimeline(result);
  }

  return mapScreenListToTimeline(result, page, pageSize);
}

const DEFAULT_GLOBAL_START = moment().subtract(30, "days").format("YYYY-MM-DD");
const DEFAULT_GLOBAL_END = moment().format("YYYY-MM-DD");

function isMultiMonth(startDate, endDate) {
  if (!startDate || !endDate) return false;
  const [startYear, startMonth] = startDate.split("-");
  const [endYear, endMonth] = endDate.split("-");
  return startYear !== endYear || startMonth !== endMonth;
}

function mapIndicators(indicators) {
  if (!indicators?.length) {
    return [];
  }

  return indicators.map((item) => ({
    id: item.Id,
    chave: item.Chave,
    nome: item.Nome,
    valor: item.Valor,
    detalhes: (item.Detalhes ?? []).map((detalhe) => ({
      perfil: detalhe.Perfil,
      qtd: detalhe.Quantidade,
    })),
    detalhesPorPeriodo: (item.DetalhesPorPeriodo ?? []).map((detalhe) => ({
      periodo: detalhe.Periodo,
      qtd: detalhe.Quantidade,
    })),
    detalhesPorTela: (item.DetalhesPorTela ?? []).map((detalhe) => ({
      tela: detalhe.Tela,
      qtd: detalhe.Quantidade,
    })),
  }));
}

function mapTopUsersDetails(items) {
  return (items ?? []).map((item) => ({
    usuario: item.Usuario,
    quantidadeAcesso: item.QuantidadeAcesso,
    funcionalidades: (item.Funcionalidades ?? []).map((funcionalidade) => ({
      nome: funcionalidade.Nome,
      quantidade: funcionalidade.Quantidade,
    })),
  }));
}

function mapTopFeaturesDetails(items) {
  return (items ?? []).map((item) => ({
    funcionalidade: item.Funcionalidade,
    quantidadeAcesso: item.QuantidadeAcesso,
    usuarioMaisAtivo: item.UsuarioMaisAtivo,
    quantidadeUsuarioMaisAtivo: item.QuantidadeUsuarioMaisAtivo,
  }));
}

function applyDashboardResult(result, setters) {
  setters.setSummaryData(result?.Summary ?? null);
  setters.setPeriodIndicators(mapIndicators(result?.Indicators));
  setters.setMonthTop10Users(result?.TopUsers ?? []);
  setters.setFeatureMoreUsed(result?.TopFeatures ?? []);
  setters.setTopUsersDetails(mapTopUsersDetails(result?.TopUsersDetalhes));
  setters.setTopFeaturesDetails(mapTopFeaturesDetails(result?.TopFeaturesDetalhes));
  setters.setMonthlyHistory(result?.MonthlyHistory ?? null);
}

const listSystem = [
  { value: "PORTALB2B", name: "B2B", id: 1 },
  { value: "APP", name: "APP", id: 2 },
];

function getDefaultModalPeriodDates() {
  const { mIni, mFim } = getMomentsForPresetDayCount(
    DEFAULT_PERIOD_OPTION.value,
  );

  return {
    initialDate: mIni.format("MM-DD-YYYY"),
    finalDate: mFim.format("MM-DD-YYYY"),
  };
}

function DashboardIndicadoresDeAcesso() {
  const snackbar = useSnackbar();

  const isMobile900 = IsMobile900();

  const [globalStartDate, setGlobalStartDate] = useState(DEFAULT_GLOBAL_START);
  const [globalEndDate, setGlobalEndDate] = useState(DEFAULT_GLOBAL_END);
  const [appliedStartDate, setAppliedStartDate] =
    useState(DEFAULT_GLOBAL_START);
  const [appliedEndDate, setAppliedEndDate] = useState(DEFAULT_GLOBAL_END);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [valueTab, setValueTab] = useState("1");
  const [clearFilters, setClearFilters] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [searching, setSearching] = useState(false);

  const [period, setPeriod] = useState(DEFAULT_PERIOD_OPTION);
  const [initialDate, setInitialDate] = useState(
    () => getDefaultModalPeriodDates().initialDate,
  );
  const [finalDate, setFinalDate] = useState(
    () => getDefaultModalPeriodDates().finalDate,
  );

  const [selectedSystem, setSelectedSystem] = useState(null);

  const [screensList, setScreensList] = useState([]);
  const [screen, setScreen] = useState("");

  const [usersList, setUsersList] = useState([]);
  const [user, setUser] = useState("");

  const [summaryData, setSummaryData] = useState(null);
  const [periodIndicators, setPeriodIndicators] = useState([]);
  const [monthTop10Users, setMonthTop10Users] = useState([]);
  const [featuresMoreUsed, setFeatureMoreUsed] = useState([]);
  const [topUsersDetails, setTopUsersDetails] = useState([]);
  const [topFeaturesDetails, setTopFeaturesDetails] = useState([]);
  const [monthlyHistory, setMonthlyHistory] = useState(null);

  const [loadingDashboard, setLoadingDashboard] = useState(true);

  const [modalLogs, setModalLogs] = useState([]);
  const [modalTotal, setModalTotal] = useState(0);
  const [modalPage, setModalPage] = useState(0);
  const [loadingModalTimeline, setLoadingModalTimeline] = useState(false);

  const initialDashboardFetchDoneRef = useRef(false);
  const modalFetchIdRef = useRef(0);
  const screenListCacheRef = useRef(null);

  const {
    connect,
    fetchDashboard,
    readyState,
    status: websocketStatus,
  } = useWebsocketLogDashboard({
    onDashboardSuccess: (result) => {
      applyDashboardResult(result, {
        setSummaryData,
        setPeriodIndicators,
        setMonthTop10Users,
        setFeatureMoreUsed,
        setTopUsersDetails,
        setTopFeaturesDetails,
        setMonthlyHistory,
      });
      setLoadingDashboard(false);
    },
    onError: (error) => {
      setLoadingDashboard(false);
      setLoadingModalTimeline(false);
      setSearching(false);
      snackbar.showMessage(
        typeof error === "string"
          ? error
          : "Erro ao carregar indicadores de acesso.",
        { severity: "error" },
      );
    },
  });

  const fetchModalTimeline = async (tab, page = 0, append = false) => {
    const fetchId = modalFetchIdRef.current + 1;
    modalFetchIdRef.current = fetchId;
    setLoadingModalTimeline(true);

    if (!append) {
      setSearching(true);
      if (tab === "1") {
        screenListCacheRef.current = null;
      }
    }

    const screenFiltersActive = hasModalScreenFilters(
      screen,
      user,
      selectedSystem,
    );

    if (
      tab === "1" &&
      append &&
      screenListCacheRef.current &&
      !screenFiltersActive
    ) {
      const {
        logs,
        total,
        page: resultPage,
      } = mapScreenListFromCache(
        screenListCacheRef.current,
        page,
        MODAL_PAGE_SIZE,
      );

      if (modalFetchIdRef.current === fetchId) {
        setModalLogs((prev) => [...prev, ...logs]);
        setModalTotal(total);
        setModalPage(resultPage);
        setLoadingModalTimeline(false);
        setSearching(false);
      }

      return;
    }

    try {
      const { Success, Result, ValidationMessage } = await Api.get(
        getModalEndpoint(tab),
        "log",
        buildModalQueryString({
          page,
          pageSize: MODAL_PAGE_SIZE,
          period,
          initialDate,
          finalDate,
          screen,
          user,
          selectedSystem,
        }),
        true,
      );

      if (modalFetchIdRef.current !== fetchId) {
        return;
      }

      if (!Success) {
        snackbar.showMessage(
          ValidationMessage || "Não foi possível carregar o histórico.",
          { severity: "error" },
        );
        if (!append) {
          setModalLogs([]);
          setModalTotal(0);
          setModalPage(0);
        }
        return;
      }

      if (tab === "1" && Array.isArray(Result) && !screenFiltersActive) {
        screenListCacheRef.current = Result.filter(
          (item) => item && !isEmptyApiItem(item),
        );
      } else if (tab === "1") {
        screenListCacheRef.current = null;
      }

      const {
        logs,
        total,
        page: resultPage,
      } = mapModalResultToTimeline(tab, Result, page, MODAL_PAGE_SIZE);

      setModalLogs((prev) => (append ? [...prev, ...logs] : logs));
      setModalTotal(total);
      setModalPage(resultPage ?? page);
    } catch (error) {
      if (modalFetchIdRef.current !== fetchId) {
        return;
      }

      snackbar.showMessage(error, { severity: "error" });
      if (!append) {
        setModalLogs([]);
        setModalTotal(0);
        setModalPage(0);
      }
    } finally {
      if (modalFetchIdRef.current === fetchId) {
        setLoadingModalTimeline(false);
        setSearching(false);
      }
    }
  };

  const handleModalSearch = async () => {
    setModalPage(0);
    await fetchModalTimeline(valueTab, 0, false);
  };

  const handleGetFiltersData = async () => {
    setLoadingFilters(true);
    await handleGetScreens();
    await handleGetUsers();
    setLoadingFilters(false);
  };

  const handleGetScreens = async () => {
    try {
      const { Success, Result } = await Api.get("screen-list", "log", "", true);
      if (Success) {
        setScreensList(Result);
      }
    } catch (error) {
      snackbar.showMessage(error, { severity: "error" });
    }
  };

  const handleGetUsers = async () => {
    try {
      const { Success, Result } = await Api.get(
        "list-user",
        "adminportal",
        "",
        true,
      );
      if (Success) {
        setUsersList(Result);
      }
    } catch (error) {
      snackbar.showMessage(error, { severity: "error" });
    }
  };

  const handleApplyGlobalFilter = () => {
    setAppliedStartDate(globalStartDate);
    setAppliedEndDate(globalEndDate);
    setLoadingDashboard(true);
    fetchDashboard(globalStartDate, globalEndDate);
  };

  const handleOpenHistoryModal = () => {
    setIsModalOpen(true);
    setModalLogs([]);
    setModalPage(0);
    fetchModalTimeline(valueTab, 0, false);
  };

  const handleLoadMoreModalTimeline = () => {
    fetchModalTimeline(valueTab, modalPage + 1, true);
  };

  const handleClearFilters = () => {
    const { initialDate: defaultInitial, finalDate: defaultFinal } =
      getDefaultModalPeriodDates();

    setClearFilters(true);
    setPeriod(DEFAULT_PERIOD_OPTION);
    setInitialDate(defaultInitial);
    setFinalDate(defaultFinal);
    setUser("");
    setScreen("");
    setSelectedSystem(null);
    screenListCacheRef.current = null;
    setModalLogs([]);
    setModalTotal(0);
    setModalPage(0);
    fetchModalTimeline(valueTab, 0, false);
  };

  const handleChangeTab = (event, newValue) => {
    setValueTab(newValue);
    setModalLogs([]);
    setModalPage(0);
    fetchModalTimeline(newValue, 0, false);
  };

  const historyMonths =
    monthlyHistory?.Meses?.length > 0
      ? monthlyHistory.Meses
      : gerarMesesDoFiltro(appliedStartDate, appliedEndDate);
  const showMultiMonthTable = isMultiMonth(appliedStartDate, appliedEndDate);

  useEffect(() => {
    if (
      user === "" &&
      screen === "" &&
      resolvePeriodValue(period) === DEFAULT_PERIOD_OPTION.value
    ) {
      setClearFilters(false);
    }
  }, [user, screen, period]);

  useEffect(() => {
    connect();
    handleGetFiltersData();
  }, []);

  useEffect(() => {
    if (
      readyState !== ReadyState.OPEN ||
      initialDashboardFetchDoneRef.current
    ) {
      return;
    }

    initialDashboardFetchDoneRef.current = true;
    setLoadingDashboard(true);
    fetchDashboard(globalStartDate, globalEndDate);
  }, [readyState, globalStartDate, globalEndDate, fetchDashboard]);

  const chartGridSize = isMobile900 ? 12 : 4;
  const isDashboardLoading = loadingDashboard || websocketStatus === "fetching";

  const handleExportGlobalDashboard = () => {
    if (isDashboardLoading) {
      snackbar.showMessage("Aguarde o carregamento dos dados do dashboard.", {
        severity: "info",
      });
      return;
    }

    try {
      exportDashboardToXlsx({
        startDate: appliedStartDate,
        endDate: appliedEndDate,
        summaryData,
        periodIndicators,
        topUsers: monthTop10Users,
        topFeatures: featuresMoreUsed,
        topUsersDetails,
        topFeaturesDetails,
        monthlyHistory,
      });
      snackbar.showMessage("Dashboard exportado com sucesso.", {
        severity: "success",
      });
    } catch (error) {
      snackbar.showMessage(
        error instanceof Error
          ? error.message
          : "Erro ao exportar o dashboard.",
        { severity: "warning" },
      );
    }
  };

  return (
    <>
      <HeaderClientes section="Indicadores de Acesso" subsection="Dashboard" />
      <Container
        maxWidth={false}
        style={{
          backgroundColor: "#f1f5f9",
          paddingBottom: "2rem",
        }}
      >
        <GlobalPeriodFilter
          startDate={globalStartDate}
          endDate={globalEndDate}
          onStartDateChange={setGlobalStartDate}
          onEndDateChange={setGlobalEndDate}
          onApply={handleApplyGlobalFilter}
          onOpenHistory={handleOpenHistoryModal}
          onExport={handleExportGlobalDashboard}
        />

        {isDashboardLoading ? (
          <Grid container spacing={3} style={{ marginBottom: "1.5rem" }}>
            <Grid item xs={isMobile900 ? 12 : 6}>
              <Skeleton
                variant="rect"
                height={140}
                style={{ borderRadius: 4 }}
              />
            </Grid>
            <Grid item xs={isMobile900 ? 12 : 6}>
              <Skeleton
                variant="rect"
                height={140}
                style={{ borderRadius: 4 }}
              />
            </Grid>
          </Grid>
        ) : (
          <SummaryCards data={summaryData} isMobile={isMobile900} />
        )}

        <Grid
          container
          spacing={3}
          alignItems="flex-start"
          style={{ marginBottom: "2rem" }}
        >
          <Grid item xs={12} lg={chartGridSize}>
            {isDashboardLoading ? (
              <Skeleton
                variant="rect"
                height={350}
                style={{ borderRadius: 4 }}
              />
            ) : (
              <PeriodIndicatorsAccordion indicators={periodIndicators} />
            )}
          </Grid>

          <Grid item xs={12} lg={chartGridSize}>
            {isDashboardLoading ? (
              <Skeleton
                variant="rect"
                height={350}
                style={{ borderRadius: 4 }}
              />
            ) : (
              <TopFeaturesChart
                data={featuresMoreUsed}
                details={topFeaturesDetails}
              />
            )}
          </Grid>

          <Grid item xs={12} lg={chartGridSize}>
            {isDashboardLoading ? (
              <Skeleton
                variant="rect"
                height={350}
                style={{ borderRadius: 4 }}
              />
            ) : (
              <CardTopUsersAccessChart
                data={monthTop10Users}
                details={topUsersDetails}
              />
            )}
          </Grid>
        </Grid>

        {showMultiMonthTable &&
          (isDashboardLoading ? (
            <Skeleton
              variant="rect"
              height={280}
              style={{ borderRadius: 4, marginBottom: "2rem" }}
            />
          ) : (
            <MonthlyHistoryTable
              months={historyMonths}
              indicadores={monthlyHistory?.Indicadores ?? []}
              startDate={appliedStartDate}
              endDate={appliedEndDate}
              onExportError={(message) =>
                snackbar.showMessage(message, { severity: "warning" })
              }
            />
          ))}
      </Container>

      <DetailHistoryModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        valueTab={valueTab}
        onChangeTab={handleChangeTab}
        filterProps={{
          handleSearch: handleModalSearch,
          searching,
          disabled: searching || loadingModalTimeline || loadingFilters,
          handleClearFilters,
          loadingFilters,
          clearFilters,
          screensList,
          screen,
          setScreen,
          usersList,
          user,
          setUser,
          listSystem,
          selectedSystem,
          setSelectedSystem,
          period,
          setPeriod,
          initialDate,
          finalDate,
          setInitialDate,
          setFinalDate,
        }}
        timelineProps={{
          logs: modalLogs,
          totalRegistros: modalTotal,
          loading: loadingModalTimeline,
          hasMore: modalLogs.length < modalTotal,
          onLoadMore: handleLoadMoreModalTimeline,
        }}
      />
    </>
  );
}

export default DashboardIndicadoresDeAcesso;
